import React from "react";
import { msgid, ngettext, t } from "ttag";

import { useAppDispatch, useAppSelector } from "../../reducers";
import { ICustomerSummary } from "../models";
import { setCustomerSearchTerm, viewStackPush } from "../reducers";
import { searchCustomers } from "../thunks";
import { CustomerDetails } from "./CustomerDetails";

const renderCustomerResultRow = (
    customer: ICustomerSummary,
    index: number,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    const viewDetails = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const component = <CustomerDetails url={customer.url} />;
        dispatch(viewStackPush({ name: t`Customer Details`, component }));
    };
    return (
        <tr key={index} className="csr-customer-search__result">
            <td>
                {customer.first_name} {customer.last_name}
            </td>
            <td>{customer.email}</td>
            <td>
                {customer.groups.map((name) => {
                    return (
                        <span className="csr-customer-group" key={name}>
                            {name}
                        </span>
                    );
                })}
            </td>
            <td className="button-group">
                <button
                    className="csr-customer-search__result-link"
                    onClick={viewDetails}
                >
                    {t`Details`}
                </button>
            </td>
        </tr>
    );
};

const renderCustomerResults = (
    results: readonly ICustomerSummary[] | undefined,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    if (!results || results.length <= 0) {
        return (
            <tr>
                <td colSpan={5}>
                    <em>{t`No Results Found`}</em>
                </td>
            </tr>
        );
    }
    return results.map((result, i) => {
        return renderCustomerResultRow(result, i, dispatch);
    });
};

export const CustomerSearch = (): React.ReactNode => {
    const searchRef = React.useRef<HTMLInputElement>(null);
    const hasMountedRef = React.useRef(false);
    const customerSearch = useAppSelector((state) => state.csr.customerSearch);
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        void dispatch(searchCustomers({ text: customerSearch.term, page: 1 }));
    }, []);

    React.useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        if (searchRef.current) {
            searchRef.current.focus();
        }
    });

    const onSearchTermChange = (event: React.FormEvent<HTMLInputElement>) => {
        dispatch(setCustomerSearchTerm(event.currentTarget.value));
    };

    const onDoSearch = (page: number, event: React.FormEvent<HTMLElement>) => {
        event.preventDefault();
        if (searchRef.current) {
            void dispatch(
                searchCustomers({ text: searchRef.current.value, page }),
            );
        }
    };

    const onPrevPage = (event: React.MouseEvent<HTMLElement>) => {
        onDoSearch(customerSearch.page - 1, event);
    };

    const onNextPage = (event: React.MouseEvent<HTMLElement>) => {
        onDoSearch(customerSearch.page + 1, event);
    };

    return (
        <div className="csr-customer-search">
            <form onSubmit={onDoSearch.bind(null, 1)}>
                <p className="csr-customer-search__help">
                    {t`To search a specific field, prefix the search term with the name of the field. For example: email:sarah@example.com.`}
                </p>
                <div className="csr-customer-search__input">
                    <input
                        type="search"
                        placeholder={t`Search`}
                        ref={searchRef}
                        value={customerSearch.term}
                        onChange={onSearchTermChange}
                    />
                </div>
                <div className="csr-customer-search__button">
                    <button type="submit">{t`Search`}</button>
                </div>
            </form>
            <div className="csr-customer-search__results">
                <p>
                    <em>
                        {ngettext(
                            msgid`Viewing page ${customerSearch.page}. Found ${customerSearch.total} total result.`,
                            `Viewing page ${customerSearch.page}. Found ${customerSearch.total} total results.`,
                            customerSearch.total,
                        )}
                    </em>
                </p>
                <p className="button-group">
                    <button
                        disabled={!customerSearch.hasPrev}
                        onClick={onPrevPage}
                        className="csr-customer-search__page-link"
                    >
                        {t`Previous Page`}
                    </button>
                    <button
                        disabled={!customerSearch.hasNext}
                        onClick={onNextPage}
                        className="csr-customer-search__page-link"
                    >
                        {t`Next Page`}
                    </button>
                </p>
                <table>
                    <thead>
                        <tr>
                            <th>{t`Name`}</th>
                            <th>{t`Email`}</th>
                            <th>{t`Groups`}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderCustomerResults(
                            customerSearch.results,
                            dispatch,
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
